import {
  Drawer as MantineDrawer,
  type DrawerProps as MantineDrawerProps,
} from '@mantine/core';
import { DRAWER_CONTENT_ZINDEX } from '@zorro/types';
import { useMobileDetector } from '@zorro/web/ui-utils/hooks/useMobileDetector';
import {
  ComponentPropsWithoutRef,
  ReactNode,
  forwardRef,
  useState,
} from 'react';

import { CloseButton } from '../CloseButton';
import styles from './BottomDrawer.module.css';
import { DrawerFooterContext } from './_drawerFooterContext';

/** Layout mode. `list` removes the body's horizontal padding so
 *  `BottomDrawer.ListItem` rows can span the full sheet width. */
export type BottomDrawerVariant = 'default' | 'list';

export interface BottomDrawerProps extends Omit<
  MantineDrawerProps,
  | 'variant'
  | 'color'
  | 'styles'
  | 'classNames'
  | 'style'
  | 'size'
  | 'position'
  | 'radius'
  | 'shadow'
  | 'padding'
  | 'offset'
  | 'bg'
> {
  /** Layout mode of the sheet body */
  variant?: BottomDrawerVariant;
  /**
   * Background color override for the sheet panel. Needed to opt out of the
   * `data-with-footer` surface (`#f7f9fb`) when a screen's design calls for the
   * plain white panel instead. Mirrors `Drawer`'s `bg`.
   */
  bg?: string;
  /** Render the (decorative) grab handle above the header */
  withHandle?: boolean;
  /**
   * Fill the available height (capped so a 48px gap remains above the sheet)
   * and let the body scroll. Matches the Figma `Expandable` variant.
   */
  isExpandable?: boolean;
  /** Content pinned below the body (typically a single full-width CTA) */
  footer?: ReactNode;
  /** Stacking order of the sheet. The overlay is rendered at `zIndex - 10`. */
  zIndex?: number;
}

/**
 * A single row inside a `variant="list"` BottomDrawer.
 * Owns the row padding Figma places on the row (not the body).
 */
const BottomDrawerListItem = ({
  className,
  children,
  ...props
}: ComponentPropsWithoutRef<'div'>) => (
  <div
    className={[styles.listItem, className].filter(Boolean).join(' ')}
    {...props}
  >
    {children}
  </div>
);

BottomDrawerListItem.displayName = 'BottomDrawer.ListItem';

const BottomDrawerRoot = forwardRef<HTMLDivElement, BottomDrawerProps>(
  (
    {
      opened,
      onClose,
      title,
      children,
      footer,
      bg,
      variant = 'default',
      withCloseButton = true,
      withHandle = false,
      isExpandable = false,
      zIndex = DRAWER_CONTENT_ZINDEX,
      className,
      'aria-label': ariaLabel,
      ...props
    },
    ref
  ) => {
    const { isMobile } = useMobileDetector();

    const [contextFooter, setContextFooter] = useState<ReactNode>(null);
    const resolvedFooter = footer ?? contextFooter;

    /* Designer requirement (verbatim, from the Figma component description):
       "This component should never render when in desktop mode, if it was
       accidentaly used, return null." Not overridable — the V2 `Tooltip`
       precedent does not expose an override either. */
    if (!isMobile) return null;

    const hasHeader = Boolean(title) || withCloseButton;

    return (
      <DrawerFooterContext.Provider value={setContextFooter}>
        <MantineDrawer.Root
          opened={opened}
          onClose={onClose}
          position="bottom"
          unstyled
          classNames={{
            overlay: styles.overlay,
            content: styles.content,
            header: styles.header,
            title: styles.title,
            body: styles.body,
          }}
          {...props}
        >
          {/* z-index must be inline: `unstyled` + portal breaks the
              CSS-variable cascade, so Mantine's --drawer-z-index never lands. */}
          <MantineDrawer.Overlay style={{ zIndex: zIndex - 10 }} />
          <MantineDrawer.Content
            ref={ref}
            className={className}
            /* Without a `title` there is no `aria-labelledby`, so the dialog
               would be announced unnamed — worst in the `List` variant, which
               has neither a title nor a close button. Consumers of the
               title-less variants should pass a meaningful `aria-label`. */
            aria-label={title ? undefined : (ariaLabel ?? 'Bottom sheet')}
            data-drawer-variant={variant}
            data-expandable={isExpandable || undefined}
            data-with-title={Boolean(title) || undefined}
            data-with-handle={withHandle || undefined}
            data-with-footer={Boolean(resolvedFooter) || undefined}
            /* Anchored inline for the same portal/cascade reason. NEVER add a
               `transform` here (or in the CSS module) — Mantine's `Transition`
               animates this element via an inline transform. */
            style={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              zIndex,
              height: isExpandable ? '100%' : undefined,
              maxHeight: isExpandable
                ? 'calc(100dvh - var(--space-48))'
                : '90dvh',
              /* Inline so it beats `.content[data-with-footer]`'s background. */
              background: bg,
            }}
          >
            {withHandle && (
              /* Static, decorative grabber. Drag / swipe-to-dismiss is NOT
                 implemented (out of scope — no `vaul`/framer-motion). */
              <div className={styles.handleRow}>
                <div className={styles.handle} aria-hidden="true" />
              </div>
            )}
            {hasHeader && (
              <MantineDrawer.Header>
                {title && <MantineDrawer.Title>{title}</MantineDrawer.Title>}
                {withCloseButton && (
                  <CloseButton
                    className={styles.closeButton}
                    onClick={onClose}
                    aria-label="Close drawer"
                  />
                )}
              </MantineDrawer.Header>
            )}
            <MantineDrawer.Body
              data-drawer-variant={variant}
              data-expandable={isExpandable || undefined}
              data-with-title={Boolean(title) || undefined}
              data-with-footer={Boolean(resolvedFooter) || undefined}
            >
              {children}
            </MantineDrawer.Body>
            {resolvedFooter && (
              <div className={styles.footer}>{resolvedFooter}</div>
            )}
          </MantineDrawer.Content>
        </MantineDrawer.Root>
      </DrawerFooterContext.Provider>
    );
  }
);

BottomDrawerRoot.displayName = 'BottomDrawer';

/**
 * Mobile-only bottom sheet. Renders `null` on desktop by design.
 *
 * All five Figma variants are reproduced through composable props:
 * - `Default`    — `withCloseButton`
 * - `Header`     — `title` + `withCloseButton`
 * - `Button`     — `title` + `withHandle` + `footer`
 * - `Expandable` — `withHandle` + `expandable`
 * - `List`       — `variant="list"` + `withCloseButton={false}`
 */
export const BottomDrawer = Object.assign(BottomDrawerRoot, {
  ListItem: BottomDrawerListItem,
});
